import { useState, useEffect } from 'react';
import { Modal, Stack, Text, Select, NumberInput, Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useInjectAdBreak } from '../useStreamMutations';
import { AD_BREAK_DURATIONS, AD_BREAK_MIN_SECONDS, AD_BREAK_MAX_SECONDS } from '@/lib/constants/stream';

interface LiveAdInjectorModalProps {
  opened: boolean;
  onClose: () => void;
  mediaId: number;
  isLive: boolean;
}

export function LiveAdInjectorModal({ opened, onClose, mediaId, isLive }: LiveAdInjectorModalProps) {
  const [selectedDuration, setSelectedDuration] = useState<string>('30');
  const [customDuration, setCustomDuration] = useState<number>(30);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const injectAdBreak = useInjectAdBreak(mediaId);

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const interval = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownRemaining]);

  // Clear cooldown when modal closes
  useEffect(() => {
    if (!opened) {
      setCooldownRemaining(0);
    }
  }, [opened]);

  async function handleInject() {
    const resolvedDuration = selectedDuration === 'custom'
      ? customDuration
      : Number(selectedDuration);

    try {
      await injectAdBreak.mutateAsync(resolvedDuration);
      setCooldownRemaining(resolvedDuration);
      notifications.show({
        title: 'Ad break injected',
        message: `A ${resolvedDuration}-second ad break has been inserted into the stream.`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Ad injection failed',
        message: err instanceof Error ? err.message : 'Failed to inject ad break.',
        color: 'red',
      });
    }
  }

  const isInCooldown = cooldownRemaining > 0;
  const isDisabled = !isLive || isInCooldown || injectAdBreak.isPending;

  return (
    <Modal opened={opened} onClose={onClose} title="Inject Ad Break" size="sm">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Insert an ad break into the live stream. Viewers will see ads for the selected duration.
        </Text>

        <Select
          label="Duration"
          data={AD_BREAK_DURATIONS as unknown as { value: string; label: string }[]}
          value={selectedDuration}
          onChange={(val) => val && setSelectedDuration(val)}
          allowDeselect={false}
        />

        {selectedDuration === 'custom' && (
          <NumberInput
            label="Custom duration (seconds)"
            value={customDuration}
            onChange={(val) => typeof val === 'number' && setCustomDuration(val)}
            min={AD_BREAK_MIN_SECONDS}
            max={AD_BREAK_MAX_SECONDS}
            clampBehavior="strict"
          />
        )}

        {isInCooldown && (
          <Text size="sm" c="dimmed" ta="center">
            Ad break in progress, {cooldownRemaining} seconds remaining...
          </Text>
        )}

        <Group justify="space-between" mt="xs">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleInject}
            disabled={isDisabled}
            loading={injectAdBreak.isPending}
          >
            Inject Ad Break
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
